import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import BudgetControlTable from './metrics/performance/BudgetControlTable';
import budgetService from 'services/budgetService';
import clientService from 'services/clientService';
import { useAuth } from 'contexts/AuthContext';
import paths from 'routes/paths';

// Funciones para mostrar fechas EXACTAMENTE como vienen de la base de datos
const formatDateWithoutTimezone = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    // Extraer directamente del string ISO sin conversiones
    // "2025-08-01T00:00:00.000Z" -> "01/08/2025, 00:00"
    const [datePart, timePart] = dateString.split('T');
    const [year, month, day] = datePart.split('-');
    const [hour, minute] = timePart.split(':');
    return `${day}/${month}/${year}, ${hour}:${minute}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Fecha inválida';
  }
};

const formatTimeWithoutTimezone = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    // Extraer directamente del string ISO sin conversiones
    // "2025-08-01T00:00:00.000Z" -> "00:00"
    const timePart = dateString.split('T')[1];
    const [hour, minute] = timePart.split(':');
    return `${hour}:${minute}`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return '00:00';
  }
};

const AccountsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { filterByAssignedClients } = useAuth();
  
  // Detectar si estamos en la sección de Branding basado en la ruta
  const isBrandingSection = location.pathname.includes('/branding');
  const campaignTypeFilter = isBrandingSection ? 'Branding' : 'Performance';
  
  const [showModal, setShowModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [orders, setOrders] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [actualSpentData, setActualSpentData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cargar datos reales al montar el componente y cuando cambie la sección
  useEffect(() => {
    loadRealData();
  }, [campaignTypeFilter]);

  const loadRealData = async () => {
    setLoading(true);
    
    try {
      // Cargar órdenes de inserción según la sección actual
      const ordersResult = await budgetService.getAllInsertionOrders(campaignTypeFilter);
      
      if (ordersResult.success) {
        // Filtrar órdenes por clientes asignados al usuario
        const filteredOrders = filterByAssignedClients(ordersResult.orders || [], 'clientId');
        setOrders(filteredOrders);
        
        // Cargar asignaciones para cada orden filtrada
        const allAssignments = [];
        for (const order of filteredOrders) {
          const assignmentsResult = await budgetService.getAssignmentsByOrderId(order.id);
          if (assignmentsResult.success) {
            allAssignments.push(...(assignmentsResult.assignments || []));
          }
        }
        setAssignments(allAssignments);
        
        const actualSpentResult = await budgetService.getAllActualSpent();
        if (actualSpentResult.success) {
          setActualSpentData(actualSpentResult.actualSpentData || []);
        } else {
          console.warn('No se pudieron cargar los gastos reales:', actualSpentResult.message);
          // Mantener array vacío para que no falle la aplicación
          setActualSpentData([]);
        }
      }
    } catch (error) {
      console.error('Error loading real data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para calcular cuánto se debería haber gastado hasta la fecha
  const calculateShouldHaveSpent = (order) => {
    const now = new Date();
    const startDate = new Date(order.dateFrom); // Ya incluye horas y minutos
    const endDate = new Date(order.dateTo);     // Ya incluye horas y minutos
    
    // Si la campaña no ha empezado, debería haber gastado 0
    if (now < startDate) return 0;
    
    // Si la campaña ya terminó, debería haber gastado todo el presupuesto
    if (now > endDate) return order.netInvestment || 0;
    
    // Calcular proporción basada en tiempo transcurrido (incluyendo horas y minutos)
    const totalDuration = endDate.getTime() - startDate.getTime(); // Milisegundos totales
    const elapsedTime = now.getTime() - startDate.getTime();       // Milisegundos transcurridos
    const progressPercentage = elapsedTime / totalDuration;
    
    // Asegurar que el progreso esté entre 0 y 1
    const clampedProgress = Math.max(0, Math.min(1, progressPercentage));
    
    return (order.netInvestment || 0) * clampedProgress;
  };

  // *** REEMPLAZAR la función de simulación con datos reales ***
  const getActualSpent = (orderId) => {
    // Buscar el gasto real desde los datos obtenidos del backend
    const spentData = actualSpentData.find(data => data.orderId === orderId);
    return spentData ? spentData.actualSpent : 0;
  };

  // Función para calcular el total asignado de una orden
  const calculateAssignedAmount = (orderId) => {
    const orderAssignments = assignments.filter(a => a.insertionOrderId === orderId);
    return orderAssignments.reduce((total, assignment) => {
      let amount = parseFloat(assignment.montoValor) || 0;
      
      // LÓGICA CORRECTA: Si es porcentaje, convertir a dólares
      const isPercentage = assignment.amountType === 'percentage' || 
                         (!assignment.amountType && amount <= 100 && amount > 0);
      
      if (isPercentage) {
        const order = orders.find(o => o.id === orderId);
        if (order && order.netInvestment) {
          amount = (amount * parseFloat(order.netInvestment)) / 100;
        }
      }
      
      return total + amount;
    }, 0);
  };

  // Transformar datos de órdenes al formato esperado por BudgetControlTable
  const budgetControlData = useMemo(() => {
    return orders.map(order => {
      const assignedAmount = calculateAssignedAmount(order.id);
      const shouldHaveSpent = calculateShouldHaveSpent(order);
      const actualSpent = getActualSpent(order.id);
      const difference = actualSpent - shouldHaveSpent;
      const percentage = shouldHaveSpent > 0 ? (actualSpent / shouldHaveSpent) * 100 : 0;

      return {
        id: order.id,
        cliente: order.clientName || 'N/A',
        businessUnit: order.businessUnit || 'N/A',
        descripcion: order.description || 'Sin descripción',
        fechaDesde: formatDateWithoutTimezone(order.dateFrom),
        horaDesde: formatTimeWithoutTimezone(order.dateFrom),
        fechaHasta: formatDateWithoutTimezone(order.dateTo),
        horaHasta: formatTimeWithoutTimezone(order.dateTo),
        moneda: order.currency || 'USD',
        inversionBruta: parseFloat(order.grossInvestment) || 0,
        inversionNeta: parseFloat(order.netInvestment) || 0,
        asignado: assignedAmount,
        deberiaGastar: shouldHaveSpent,
        gastado: actualSpent,
        inversionOffset: difference,
        porcentajeOffset: percentage,
        status: percentage > 110 ? 'danger' : percentage < 90 ? 'warning' : 'success'
      };
    });
  }, [orders, assignments, actualSpentData]);

  // Cargar cuentas reales desde la API
  const [accounts, setAccounts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // Cargar cuentas reales al montar el componente
  useEffect(() => {
    loadRealAccounts();
  }, []);

  const loadRealAccounts = async () => {
    setLoadingAccounts(true);
    try {
      // Cargar cuentas y clientes en paralelo
      const [accountsResult, clientsResult] = await Promise.all([
        clientService.getAllAccounts(),
        clientService.getAllClients()
      ]);

      if (accountsResult.success) {
        // Transformar datos de la API al formato esperado por la UI
        const transformedAccounts = accountsResult.accounts.map(account => ({
          id: account.AccountIdentifier || account.AccountID.toString(),
          name: account.AccountName,
          client: account.client?.ClientName || 'Cliente no encontrado',
          platform: account.AccountType,
          funnelStage: account.FunnelStage || null,
          campaignType: account.CampaignType || null,
          status: account.Active ? 'active' : 'paused',
          budget: 0,
          spent: 0,
          campaigns: 0,
          lastUpdate: new Date().toISOString().split('T')[0],
          accountId: account.AccountID,
          clientId: account.ClientID
        }));
        setAccounts(transformedAccounts);
      } else {
        console.error('Error loading accounts:', accountsResult.error);
        toast.error('Error al cargar las cuentas');
      }

      if (clientsResult.success) {
        setClients(clientsResult.clients || []);
      }
    } catch (error) {
      console.error('Error loading real accounts:', error);
      toast.error('Error al cargar los datos de cuentas');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleAccountClick = (account) => {
    setSelectedAccount(account);
    setShowModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'suspended': return 'danger';
      default: return 'secondary';
    }
  };

  const getSpentPercentage = (spent, budget) => {
    return budget > 0 ? (spent / budget) * 100 : 0;
  };

  return (
    <Container fluid className="px-3">
      <div className="pb-4">
        {/* Header */}
        <div className="mb-4">
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-2">
                <FontAwesomeIcon 
                  icon={isBrandingSection ? "palette" : "credit-card"} 
                  className={`me-2 ${isBrandingSection ? 'text-info' : 'text-primary'}`} 
                />
                Gestión de Cuentas
                <Badge 
                  bg={isBrandingSection ? "info" : "primary"} 
                  className="ms-2 fs-10"
                >
                  {campaignTypeFilter}
                </Badge>
              </h2>
              <h5 className="text-700 fw-semi-bold">
                Administración de cuentas publicitarias y campañas ({campaignTypeFilter})
              </h5>
            </Col>
            <Col xs="auto">
              <Button variant="primary" className="me-2" onClick={() => navigate(paths.performanceAccountsManagement)}>
                <FontAwesomeIcon icon="plus" className="me-1" />
                Gestionar Cuentas
              </Button>
              <Button variant="outline-primary" onClick={loadRealAccounts} disabled={loadingAccounts}>
                <FontAwesomeIcon icon="sync-alt" className="me-1" />
                {loadingAccounts ? 'Sincronizando...' : 'Sincronizar'}
              </Button>
            </Col>
          </Row>
        </div>

        {/* Cards de Resumen */}
        <Row className="g-3 mb-4">
          <Col lg={3} md={6}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="bg-primary-soft rounded-circle p-3 me-3">
                    <FontAwesomeIcon icon="credit-card" className="text-primary" size="lg" />
                  </div>
                  <div>
                    <h6 className="mb-1 text-700">Cuentas Activas</h6>
                    <h3 className="mb-0 text-primary">{accounts.filter(a => a.status === 'active').length}</h3>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="bg-success-soft rounded-circle p-3 me-3">
                    <FontAwesomeIcon icon="dollar-sign" className="text-success" size="lg" />
                  </div>
                  <div>
                    <h6 className="mb-1 text-700">Presupuesto Total</h6>
                    <h3 className="mb-0 text-success">
                      ${accounts.reduce((sum, acc) => sum + acc.budget, 0).toLocaleString()}
                    </h3>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="bg-warning-soft rounded-circle p-3 me-3">
                    <FontAwesomeIcon icon="chart-bar" className="text-warning" size="lg" />
                  </div>
                  <div>
                    <h6 className="mb-1 text-700">Gastado Total</h6>
                    <h3 className="mb-0 text-warning">
                      ${accounts.reduce((sum, acc) => sum + acc.spent, 0).toLocaleString()}
                    </h3>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="bg-info-soft rounded-circle p-3 me-3">
                    <FontAwesomeIcon icon="chart-line" className="text-info" size="lg" />
                  </div>
                  <div>
                    <h6 className="mb-1 text-700">Total Campañas</h6>
                    <h3 className="mb-0 text-info">
                      {accounts.reduce((sum, acc) => sum + acc.campaigns, 0)}
                    </h3>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Grid de Cuentas */}
        <Row className="g-3 mb-4">
          {accounts.map(account => {
            const spentPercentage = getSpentPercentage(account.spent, account.budget);
            const isOverBudget = spentPercentage > 100;
            
            return (
              <Col lg={4} md={6} key={account.id}>
                <Card 
                  className="h-100 shadow-sm cursor-pointer account-card"
                  onClick={() => handleAccountClick(account)}
                  style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                  onMouseEnter={(e) => e.target.style.transform = 'translateY(-5px)'}
                  onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h6 className="mb-1 fw-bold">{account.name}</h6>
                        <small className="text-muted">ID: {account.id}</small>
                      </div>
                      <Badge bg={getStatusColor(account.status)}>
                        {account.status}
                      </Badge>
                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="text-600">Cliente:</small>
                        <Badge bg="light" text="dark">{account.client}</Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="text-600">Plataforma:</small>
                        <span className="fs-11 fw-semibold">{account.platform}</span>
                      </div>
                      {account.funnelStage && (
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="text-600">Etapa Funnel:</small>
                          <Badge bg={account.funnelStage === 'Upper' ? 'info' : account.funnelStage === 'Mid' ? 'warning' : 'success'} style={{ fontSize: '0.7rem' }}>
                            {account.funnelStage === 'Upper' ? 'Upper (Notoriedad)' : account.funnelStage === 'Mid' ? 'Mid (Consideración)' : 'Lower (Acciones)'}
                          </Badge>
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-600">Progreso:</small>
                        <small className={`fw-bold ${isOverBudget ? 'text-danger' : 'text-primary'}`}>
                          {spentPercentage.toFixed(1)}%
                        </small>
                      </div>
                      <div className="progress" style={{ height: '6px' }}>
                        <div 
                          className={`progress-bar ${isOverBudget ? 'bg-danger' : 'bg-primary'}`}
                          style={{ width: `${Math.min(spentPercentage, 100)}%` }}
                        />
                      </div>
                    </div>

                    <Row className="g-2 text-center">
                      <Col>
                        <div className="border-end">
                          <div className="fw-bold text-success">${account.budget.toLocaleString()}</div>
                          <small className="text-muted">Presupuesto</small>
                        </div>
                      </Col>
                      <Col>
                        <div className="border-end">
                          <div className={`fw-bold ${isOverBudget ? 'text-danger' : 'text-warning'}`}>
                            ${account.spent.toLocaleString()}
                          </div>
                          <small className="text-muted">Gastado</small>
                        </div>
                      </Col>
                      <Col>
                        <div>
                          <div className="fw-bold text-info">{account.campaigns}</div>
                          <small className="text-muted">Campañas</small>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* Tabla de Órdenes */}
        <Card className="shadow-sm">
          <Card.Header>
            <h5 className="mb-0">
              <FontAwesomeIcon icon="table" className="me-2 text-primary" />
              Órdenes de Inversión
            </h5>
          </Card.Header>
          <Card.Body className="p-0">
            <BudgetControlTable data={budgetControlData} />
          </Card.Body>
        </Card>

        {/* Modal de Detalles de Cuenta */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon="credit-card" className="me-2" />
              Detalles de Cuenta
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedAccount && (
              <Row className="g-3">
                <Col md={6}>
                  <Card className="border-0 bg-light">
                    <Card.Body>
                      <h6 className="text-primary">Información General</h6>
                      <p><strong>Nombre:</strong> {selectedAccount.name}</p>
                      <p><strong>ID:</strong> {selectedAccount.id}</p>
                      <p><strong>Cliente:</strong> {selectedAccount.client}</p>
                      <p><strong>Plataforma:</strong> {selectedAccount.platform}</p>
                      <p><strong>Estado:</strong> 
                        <Badge bg={getStatusColor(selectedAccount.status)} className="ms-2">
                          {selectedAccount.status}
                        </Badge>
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border-0 bg-light">
                    <Card.Body>
                      <h6 className="text-success">Métricas</h6>
                      <p><strong>Presupuesto:</strong> ${selectedAccount.budget.toLocaleString()}</p>
                      <p><strong>Gastado:</strong> ${selectedAccount.spent.toLocaleString()}</p>
                      <p><strong>Restante:</strong> ${(selectedAccount.budget - selectedAccount.spent).toLocaleString()}</p>
                      <p><strong>Campañas:</strong> {selectedAccount.campaigns}</p>
                      <p><strong>Última actualización:</strong> {selectedAccount.lastUpdate}</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
              Cerrar
            </Button>
            <Button variant="primary">
              <FontAwesomeIcon icon="edit" className="me-1" />
              Editar Cuenta
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
};

export default AccountsPage; 